/**
 * CinematicLoadingScreen — shared full-page loading effect used across
 * Blitz quiz loading, KIBA chat loading, and Battle loading.
 *
 * Props:
 *   avatarSrc        — hero portrait image (left card)
 *   agentName        — name shown on the card badge (e.g. "KIBA")
 *   headline         — big text, can include <span> JSX (passed as ReactNode)
 *   statusLabel      — small blinking badge top-left (e.g. "CONNECTING LIVE AVATAR")
 *   metaLine         — small mono text below headline (e.g. "LIVE AVATAR · ENGLISH")
 *   progressDuration — seconds for the progress bar (default 3)
 *   bgAvatars        — array of image src strings for the background mosaic
 *   extraContent     — optional JSX rendered below the meta line (e.g. VS badge)
 */
import { useEffect, useState } from "react";

const DEFAULT_BG = [
  "/male-1.png", "/male-2.png", "/male-3.png", "/male-4.png",
  "/fe-1.png",   "/fe-2.png",   "/fe-3.png",   "/fe-4.png",
  "/fe-6.svg",   "/33-boy.png",
];

const PARTICLES = [
  { left: "10%", top: "30%", delay: "0s",   dx: "8px"  },
  { left: "80%", top: "50%", delay: "0.6s", dx: "-10px" },
  { left: "50%", top: "70%", delay: "1.1s", dx: "15px"  },
  { left: "25%", top: "60%", delay: "1.8s", dx: "-6px"  },
  { left: "70%", top: "25%", delay: "0.3s", dx: "12px"  },
  { left: "60%", top: "80%", delay: "2.1s", dx: "-14px" },
];

export default function CinematicLoadingScreen({
  avatarSrc = "/fe-1.png",
  agentName = "AI",
  headline,
  statusLabel = "LOADING",
  metaLine = "",
  progressDuration = 3,
  bgAvatars = DEFAULT_BG,
  extraContent = null,
}) {
  const [dots, setDots] = useState(".");
  useEffect(() => {
    const id = setInterval(() => setDots((d) => (d.length >= 3 ? "." : d + ".")), 500);
    return () => clearInterval(id);
  }, []);

  // Duplicate the mosaic array for seamless loop
  const mosaic = [...bgAvatars, ...bgAvatars, ...bgAvatars, ...bgAvatars];

  return (
    <div
      className="relative z-10 slide-up"
      style={{ minHeight: "85vh", display: "flex", flexDirection: "column", justifyContent: "center" }}
    >
      <style>{`
        @keyframes cl-float    { 0%,100% { transform: translateY(0px) scale(1); }  50% { transform: translateY(-14px) scale(1.02); } }
        @keyframes cl-scan     { 0% { top: -4px; opacity: 0.7; } 80% { opacity: 0.7; } 100% { top: 100%; opacity: 0; } }
        @keyframes cl-ring1    { 0%,100% { transform: scale(1); opacity: 0.6; }    50% { transform: scale(1.18); opacity: 0.1; } }
        @keyframes cl-ring2    { 0%,100% { transform: scale(1); opacity: 0.35; }   50% { transform: scale(1.35); opacity: 0.05; } }
        @keyframes cl-particle { 0% { transform: translateY(0) translateX(0) scale(1); opacity: 0.8; } 100% { transform: translateY(-90px) translateX(var(--cl-dx,12px)) scale(0); opacity: 0; } }
        @keyframes cl-mosaic   { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes cl-glow     { 0%,100% { text-shadow: 0 0 20px rgba(230,57,70,0.5); } 50% { text-shadow: 0 0 50px rgba(230,57,70,1), 0 0 80px rgba(230,57,70,0.4); } }
        @keyframes cl-bar      { 0% { width: 0%; } 85% { width: 92%; } 100% { width: 100%; } }
        @keyframes cl-blink    { 0%,100% { opacity: 1; } 50% { opacity: 0.2; } }
        @keyframes cl-soundbar { from { height: 5px; } to { height: 28px; } }
      `}</style>

      {/* ── Background: full-viewport scrolling avatar mosaic ── */}
      <div style={{
        position: "absolute", top: 0, bottom: 0,
        left: "calc(-50vw + 50%)", width: "100vw",
        overflow: "hidden", opacity: 0.32, zIndex: 0, pointerEvents: "none",
      }}>
        <div style={{
          display: "flex", gap: 12, height: "100%", alignItems: "center",
          width: "max-content",
          animation: "cl-mosaic 44s linear infinite",
        }}>
          {mosaic.map((src, i) => (
            <img key={i} src={src} alt="" style={{
              height: "72vh", width: "auto", objectFit: "cover",
              borderRadius: 12, filter: "grayscale(55%) brightness(0.65)",
              flexShrink: 0,
            }} />
          ))}
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{
        position: "relative", zIndex: 1,
        display: "flex", gap: 64, alignItems: "center", justifyContent: "center",
        padding: "0 24px",
      }}>

        {/* ── LEFT: Hero avatar card ── */}
        <div style={{ position: "relative", flexShrink: 0, width: 320, height: 420 }}>
          {/* Pulsing rings */}
          <div style={{
            position: "absolute", inset: "-30px", borderRadius: "50%",
            border: "2px solid rgba(230,57,70,0.5)",
            animation: "cl-ring1 2.2s ease-in-out infinite",
          }} />
          <div style={{
            position: "absolute", inset: "-55px", borderRadius: "50%",
            border: "1px solid rgba(230,57,70,0.3)",
            animation: "cl-ring2 2.2s ease-in-out 0.4s infinite",
          }} />
          {/* Glow backdrop */}
          <div style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(ellipse at 50% 80%, rgba(230,57,70,0.45) 0%, transparent 65%)",
            borderRadius: 24, filter: "blur(20px)",
          }} />
          {/* Card */}
          <div style={{
            position: "relative", width: "100%", height: "100%",
            borderRadius: 20, overflow: "hidden",
            border: "2px solid rgba(230,57,70,0.6)",
            boxShadow: "0 0 40px rgba(230,57,70,0.4), 0 0 80px rgba(230,57,70,0.15)",
            animation: "cl-float 3.5s ease-in-out infinite",
            background: "#000",
          }}>
            <img src={avatarSrc} alt={agentName}
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
            {/* Scan line */}
            <div style={{
              position: "absolute", left: 0, right: 0, height: 3,
              background: "linear-gradient(90deg, transparent, rgba(230,57,70,0.9), transparent)",
              animation: "cl-scan 2.5s linear infinite", pointerEvents: "none",
            }} />
            {/* Bottom vignette */}
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0, height: "45%",
              background: "linear-gradient(to top, rgba(6,10,31,0.95), transparent)",
            }} />
            {/* Name badge */}
            <div style={{ position: "absolute", bottom: 18, left: 0, right: 0, textAlign: "center" }}>
              <div style={{
                fontFamily: "var(--font-display, sans-serif)",
                fontSize: 28, fontWeight: 900, letterSpacing: "0.08em",
                textTransform: "uppercase", color: "#fff",
                animation: "cl-glow 2s ease-in-out infinite",
              }}>{agentName}</div>
              <div style={{
                fontFamily: "monospace", fontSize: 10, letterSpacing: "0.25em",
                textTransform: "uppercase", color: "rgba(230,57,70,0.8)", marginTop: 4,
              }}>PROCTORLY AI</div>
            </div>
          </div>
          {/* Floating particles */}
          {PARTICLES.map((p, i) => (
            <div key={i} style={{
              position: "absolute", left: p.left, top: p.top,
              width: 6, height: 6, borderRadius: "50%",
              background: "var(--kl-red)", boxShadow: "0 0 8px rgba(230,57,70,0.9)",
              "--cl-dx": p.dx,
              animation: `cl-particle 2s ease-out ${p.delay} infinite`,
            }} />
          ))}
        </div>

        {/* ── RIGHT: Status text ── */}
        <div style={{ maxWidth: 400 }}>
          {/* Status badge */}
          <div style={{
            fontFamily: "monospace", fontSize: 11, letterSpacing: "0.3em",
            textTransform: "uppercase", color: "rgba(230,57,70,0.7)", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <div style={{
              width: 7, height: 7, borderRadius: "50%", background: "var(--kl-red)",
              animation: "cl-blink 1s ease-in-out infinite",
            }} />
            {statusLabel}{dots}
          </div>

          {/* Headline */}
          <div style={{
            fontFamily: "var(--font-display, sans-serif)",
            fontSize: "clamp(2.4rem, 5vw, 3.4rem)", fontWeight: 900,
            lineHeight: 0.95, textTransform: "uppercase", color: "#fff",
            letterSpacing: "-0.01em",
          }}>
            {headline ?? (
              <>{agentName}<br />
                <span style={{ color: "var(--kl-red)" }}>IS JOINING</span><br />
                THE ROOM.
              </>
            )}
          </div>

          {/* Meta line */}
          {metaLine && (
            <div style={{
              marginTop: 24, fontFamily: "monospace", fontSize: 12,
              color: "rgba(255,255,255,0.4)", letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}>
              {metaLine}
            </div>
          )}

          {/* Progress bar */}
          <div style={{
            marginTop: 20, height: 3, borderRadius: 2,
            background: "rgba(255,255,255,0.08)", overflow: "hidden",
          }}>
            <div style={{
              height: "100%", borderRadius: 2,
              background: "linear-gradient(90deg, var(--kl-red), #ff6b6b)",
              animation: `cl-bar ${progressDuration}s ease-out forwards`,
              boxShadow: "0 0 12px rgba(230,57,70,0.8)",
            }} />
          </div>

          {/* Sound bars */}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 4, marginTop: 24, height: 30 }}>
            {[14, 22, 18, 28, 12, 20, 16, 24, 10, 18].map((h, i) => (
              <div key={i} style={{
                width: 4, borderRadius: 2, background: "var(--kl-red)",
                opacity: 0.7,
                animation: `cl-soundbar 0.5s ease-in-out ${(i * 0.07).toFixed(2)}s infinite alternate`,
                minHeight: 4,
              }} />
            ))}
          </div>

          {/* Optional extra content (e.g. VS badge in Battle) */}
          {extraContent}
        </div>
      </div>
    </div>
  );
}
